import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'shared/components/ui/checkbox';
import { ResizableTextArea } from 'shared/components/ui/resizable-text-area';
import { useClickOutside } from 'shared/hooks/use-click-outside';
import { useKeysDown } from 'shared/hooks/use-keys-down';

import * as Styled from './sub-task-card.style';

export type NewSubTaskCardProps = {
  onSubmit: (name: string) => void;
  onChange?: (name: string) => void;
  disabled?: boolean;
};

export const NewSubTaskCard: React.FC<NewSubTaskCardProps> = ({
  onSubmit,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLTextAreaElement>(null);
  const [updatedName, setUpdatedName] = useState('');

  const _onClick = () => !disabled && nameInputRef.current?.focus();

  const _onChange = (name: string) => {
    setUpdatedName(name);
    onChange?.(name);
  };

  const submit = () => {
    updatedName && onSubmit(updatedName);
    setUpdatedName('');
    onChange?.('');
  };

  useKeysDown(
    ['Enter'],
    (e) => {
      if (!disabled && document.activeElement === nameInputRef.current) {
        e.preventDefault();

        submit();
        _onClick();
        return;
      }
    },
    { otherKeysInCombinationAllowed: true },
  );

  useClickOutside(containerRef, submit);

  return (
    <Styled.Container ref={containerRef} onClick={_onClick}>
      <Styled.CheckboxContainer>
        <Checkbox checked={false} readOnly isPlaceholder={!updatedName} />
      </Styled.CheckboxContainer>

      <Styled.TitleContainer>
        <ResizableTextArea
          ref={nameInputRef}
          value={updatedName}
          onChange={_onChange}
          placeholder={t('forms.new-task.sub-task.title.placeholder')}
          disabled={disabled}
        />
      </Styled.TitleContainer>
    </Styled.Container>
  );
};
